import { Link, useNavigate } from 'react-router-dom';
import { FaBackward } from 'react-icons/fa';
import { Mail, ChevronLeft, ChevronRight } from 'lucide-react';
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Logo from '../assest/l.png';
import {
  registerMatrimony,
  getFounderKyc,
  getCountries,
  getStates,
  getCities,
  getReligions,
  getCastes,
  getGothrams,
  getDhoshams,
  getOccupations,
  getZodiac,
  getStar,
  getEducation,
  getMotherTongue,
  sendOtp,
  verifyOtp,
  checkEmail
} from '../utils/api';
import { toast } from 'react-toastify';
import { useAuth } from '../utils/authContext';

export default function MatrimonyReg() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const steps = [
    'Basic Info',
    'Personal Details',
    'Family & Professional',
    'Account Setup'
  ];
  const typewriterTexts = [
    'Find your perfect life partner',
    'Start your journey with us',
    'Register for free today'
  ];

  const [formData, setFormData] = useState({
    fullName: '',
    startupId: '',
    founderId: '',
    email: '',
    phoneNumber: '',
    gender: '',
    dateOfBirth: '',
    weight: '',
    maritalStatus: '',
    religion: '',
    caste: '',
    motherTongue: '',
    country: '',
    state: '',
    city: '',
    occupation: '',
    education: '',
    annualIncome: '',
    fatherName: '',
    motherName: '',
    siblings: '',
    familyStatus: '',
    familyType: '',
    familyValues: '',
    password: '',
    confirmPassword: '',
    aboutMe: '',
    hobbies: '',
    companyName: '',
    expectedRevenue: '',
    founderVision: ''
  });

  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [religions, setReligions] = useState([]);
  const [castes, setCastes] = useState([]);
  const [gothrams, setGothrams] = useState([]);
  const [dhoshams, setDhoshams] = useState([]);
  const [occupations, setOccupations] = useState([]);
  const [zodiacs, setZodiacs] = useState([]);
  const [stars, setStars] = useState([]);
  const [educations, setEducations] = useState([]);
  const [motherTongues, setMotherTongues] = useState([]);

  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [emailOtp, setEmailOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [sendingOtp, setSendingOtp] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const [otpTimer, setOtpTimer] = useState(0);

  const { loginMatrimony } = useAuth();

  useEffect(() => {
    (async () => {
      try {
        const [
          countriesRes,
          religionsRes,
          castesRes,
          gothramsRes,
          dhoshamsRes,
          occupationsRes,
          zodiacsRes,
          starsRes,
          educationsRes,
          motherTonguesRes
        ] = await Promise.all([
          getCountries(),
          getReligions(),
          getCastes(),
          getGothrams(),
          getDhoshams(),
          getOccupations(),
          getZodiac(),
          getStar(),
          getEducation(),
          getMotherTongue()
        ]);
        setCountries(countriesRes);
        setReligions(religionsRes);
        setCastes(castesRes);
        setGothrams(gothramsRes);
        setDhoshams(dhoshamsRes);
        setOccupations(occupationsRes);
        setZodiacs(zodiacsRes);
        setStars(starsRes);
        setEducations(educationsRes);
        setMotherTongues(motherTonguesRes);
      } catch (err) {
        console.error(err);
        toast.error('Failed to load dropdown data');
      }
    })();
  }, []);

  const [displayText, setDisplayText] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);
  useEffect(() => {
    let charIndex = 0;
    let forward = true;
    const tick = () => {
      const txt = typewriterTexts[currentIndex];
      if (forward) {
        setDisplayText(txt.slice(0, ++charIndex));
        if (charIndex >= txt.length) forward = false;
      } else {
        setDisplayText(txt.slice(0, --charIndex));
        if (charIndex <= 0) {
          forward = true;
          setCurrentIndex(i => (i + 1) % typewriterTexts.length);
        }
      }
    };
    const id = setInterval(tick, 100);
    return () => clearInterval(id);
  }, [currentIndex]);

  useEffect(() => {
    if (!formData.country) return;
    (async () => {
      const sel = countries.find(c => c.name === formData.country);
      const st = sel ? await getStates(sel._id) : [];
      setStates(st);
      setFormData(f => ({ ...f, state: '', city: '' }));
      setCities([]);
    })();
  }, [formData.country, countries]);

  useEffect(() => {
    if (!formData.state) return;
    (async () => {
      const sel = states.find(s => s.name === formData.state);
      const ci = sel ? await getCities(sel._id) : [];
      setCities(ci);
      setFormData(f => ({ ...f, city: '' }))
    })();
  }, [formData.state, states]);

  useEffect(() => {
    let timer;
    if (otpTimer > 0) {
      timer = setTimeout(() => setOtpTimer(otpTimer - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [otpTimer]);

  const isEntrepreneurOccupation = (occ) => {
    if (!occ) return false;
    const s = occ.toLowerCase();
    return s.includes('entrepreneur') || s.includes('startup') || s.includes('founder');
  };

  const handleSendOtp = async () => {
    setError(null);
    const email = formData.email && formData.email.toLowerCase().trim();
    if (!email) {
      setError('Please enter your email before sending OTP.');
      return;
    }

    try {
      setSendingOtp(true);
      const res = await sendOtp(email);
      const success = res?.success ?? res?.data?.success;
      if (success) {
        setOtpSent(true);
        setOtpTimer(60);
        toast.success('OTP sent to your email');
      } else {
        const message = res?.message ?? res?.data?.message ?? 'Failed to send OTP';
        throw new Error(message);
      }
    } catch (err) {
      console.error('Send OTP error:', err);
      setError(err.message || 'Could not send OTP');
      toast.error(err.message || 'Could not send OTP');
    } finally {
      setSendingOtp(false);
    }
  };

  const handleVerifyOtp = async () => {
    setError(null);
    const email = formData.email && formData.email.toLowerCase().trim();
    if (!email || !emailOtp) {
      setError('Enter email and OTP to verify.');
      return;
    }
    try {
      setVerifyingOtp(true);
      const res = await verifyOtp(email, emailOtp);
      const success = res?.success ?? res?.data?.success;
      if (success) {
        setOtpVerified(true);
        toast.success('Email verified');
      } else {
        const message = res?.message ?? res?.data?.message ?? 'OTP verification failed';
        throw new Error(message);
      }
    } catch (err) {
      console.error('Verify OTP error:', err);
      setError(err.message || 'OTP verification failed');
      toast.error(err.message || 'OTP verification failed');
    } finally {
      setVerifyingOtp(false);
    }
  };

  const handleChange = key => e => setFormData(f => ({ ...f, [key]: e.target.value }));

  const handleVerify = async () => {
    setError(null);
    const id = formData.startupId.trim();
    if (!id) {
      return setError('Please enter a valid Startup ID.');
    }

    try {
      setVerifyLoading(true);

      const resp = await getFounderKyc(id);
      console.log('raw resp:', resp);

      if (!resp?.founderKyc) {
        throw new Error('No KYC record found for that Startup ID.');
      }

      const kyc = resp.founderKyc;
      console.log('founderKyc:', kyc);

      // 3) ensure approved
      if (kyc.verification_status !== 'approved') {
        throw new Error(`KYC status is "${kyc.verification_status}", not approved.`);
      }

      // 4) ensure there's an ID
      if (!kyc.founderId) {
        throw new Error('Founder ID missing in KYC record.');
      }

      // 5) success! populate form
      setFormData(f => ({
        ...f,
        fullName: kyc.founderName || f.fullName,
        email: kyc.email || f.email,
        phoneNumber: kyc.phoneNumber || f.phone,
        founderId: kyc.founderId,
        occupation: 'Entrepreneur / Startup Founder'
      }));
      localStorage.setItem('UserFounderId', kyc.founderId);
      setIsVerified(true);
      toast.success('Founder verified!');
    } catch (err) {
      console.error('Verify error:', err);
      setError(err.message);
      localStorage.removeItem('UserFounderId');
      setIsVerified(false);
    } finally {
      setVerifyLoading(false);
    }
  };

  const nextStep = () => {
    if (currentStep === 0) {
      if (!formData.fullName || !formData.email || !formData.phoneNumber) {
        setError('Please fill all required fields');
        return;
      }
      if (formData.startupId && !isVerified) {
        setError('Please verify your Startup ID');
        return;
      }
      if (!otpVerified) {
        setError('Please verify your email using the OTP before proceeding');
        toast.error('Please verify your email using the OTP before proceeding');
        return;
      }
    }

    if (currentStep === 1) {
      if (!formData.gender || !formData.dateOfBirth || !formData.religion) {
        setError('Please fill all required fields');
        return;
      }
    }

    if (currentStep === 2) {
      if (!formData.founderId && !formData.occupation) {
        setError('Please select your occupation (or verify your Founder ID).');
        return;
      }
      if (isEntrepreneurOccupation(formData.occupation) || formData.founderId) {
        if (!formData.companyName?.trim() || !formData.founderVision?.trim()) {
          setError('Please enter Startup/Company name and Founder Vision for Entrepreneur occupation.');
          return;
        }
      }
    }

    setError(null);
    setCurrentStep(s => Math.min(s + 1, steps.length - 1));
  };

  const prevStep = () => {
    setError(null);
    setCurrentStep(s => Math.max(s - 1, 0));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setError(null);
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (!otpVerified) {
      setError('Please verify your email using the OTP before registering.');
      toast.error('Please verify your email using the OTP before registering.');
      return;
    }

    const payload = {
      full_name: formData.fullName.trim(),
      startup_id: formData.startupId.trim(),
      founder_id: formData.founderId.trim(),
      email: formData.email.toLowerCase().trim(),
      phone_number: `+91${formData.phoneNumber.trim()}`,
      gender: formData.gender,
      dob: formData.dateOfBirth,
      weight: formData.weight,
      marital_status: formData.maritalStatus,
      religion: formData.religion,
      caste: formData.caste,
      mother_tongue: formData.motherTongue,
      country: formData.country,
      state: formData.country === 'India' ? formData.state.trim() : '',
      city: formData.city.trim(),
      occupation: formData.occupation || null,
      education: formData.education,
      annual_income: formData.annualIncome,
      father_name: formData.fatherName,
      mother_name: formData.motherName,
      siblings: formData.siblings,
      family_status: formData.familyStatus,
      family_type: formData.familyType,
      family_values: formData.familyValues,
      about_me: formData.aboutMe,
      hobbies: formData.hobbies,
      password: formData.password,
      confirm_password: formData.confirmPassword,
      company_name: formData.companyName?.trim() || null,
      expected_revenue: formData.expectedRevenue?.trim() || null,
      founder_vision: formData.founderVision?.trim() || null,
    };
    try {
      setLoading(true);
      const res = await registerMatrimony(payload);
      const user = res.user || res.data;
      if (res.token) {
        localStorage.setItem('matrimonyToken', res.token);
        localStorage.setItem('matrimonyUserId', user.id || user._id);
        loginMatrimony(res.token);
      }
      toast.success('Registration successful!');
      navigate('/matrimony-preference');
    } catch (err) {
      console.error(err);
      setError(err.message || 'Registration failed');
      toast.error(err.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const renderStepProgress = () => (
    <div className="flex justify-between mb-8 overflow-x-auto py-2">
      {steps.map((step, idx) => (
        <div key={idx} className="flex flex-col items-center min-w-[60px]">
          <div className={`w-8 h-8 md:w-10 md:h-10 rounded-full flex items-center justify-center ${idx <= currentStep ? 'bg-[#4b154c] text-white' : 'bg-gray-200 text-gray-600'}`}>
            {idx + 1}
          </div>
          <span className={`text-xs mt-2 text-center ${idx === currentStep ? 'font-bold text-[#4b154c]' : 'text-gray-500'}`}>
            {step}
          </span>
        </div>
      ))}
    </div>
  );

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 0:
        return (
          <div className="space-y-5">
            <div className="flex flex-col">
              <label htmlFor="fullName" className="text-gray-700 font-medium mb-1">
                Full Name <span className="text-red-500">*</span>
              </label>
              <input
                id="fullName"
                placeholder="Enter Name"
                className="w-full bg-gray-200 placeholder-gray-500 rounded-full px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-300 !border-none"
                value={formData.fullName}
                onChange={handleChange('fullName')}
                required
              />
            </div>

            <div className="flex flex-col sm:flex-row sm:items-end gap-2">
              <div className="flex-1 flex flex-col">
                <label htmlFor="startupId" className="text-gray-700 font-medium mb-1">
                  Startup ID (Founder Code)
                </label>
                <input
                  id="startupId"
                  placeholder="e.g. TACF0602"
                  className="w-full bg-gray-200 placeholder-gray-500 rounded-full px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-300 !border-none"
                  value={formData.startupId}
                  onChange={handleChange('startupId')}
                  disabled={isVerified}
                />
              </div>
              <button
                type="button"
                onClick={handleVerify}
                disabled={verifyLoading || !formData.startupId || isVerified}
                className="px-4 py-2 bg-[#4b154c] text-white rounded-full hover:opacity-90 disabled:opacity-50 sm:mb-1"
              >
                {verifyLoading ? 'Verifying…' : isVerified ? 'Verified' : 'Verify'}
              </button>
            </div>

            {/* Entrepreneur trigger */}
            <div className="text-left">
              <Link
                to="/startup-reg"
                className="text-indigo-600 text-sm hover:underline"
              >
                If you are entrepreneur, click here to register
              </Link>
            </div>

            <div className="flex flex-col">
              <label htmlFor="email" className="text-gray-700 font-medium mb-1">
                Email <span className="text-red-500">*</span>
              </label>
              <div className="relative flex items-center gap-2">
                <div className="relative flex-1">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    id="email"
                    type="email"
                    placeholder="Type Email"
                    className="w-full bg-gray-200 placeholder-gray-500 rounded-full pl-10 pr-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-300 !border-none"
                    value={formData.email}
                    onChange={(e) => {
                      setFormData(f => ({ ...f, email: e.target.value }));
                      setOtpSent(false);
                      setOtpVerified(false);
                      setEmailOtp('');
                    }}
                    required
                  />
                </div>

                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={handleSendOtp}
                    disabled={sendingOtp || otpTimer > 0 || !formData.email}
                    className="px-3 py-2 bg-[#4b154c] text-white rounded-full hover:opacity-90 disabled:opacity-50"
                  >
                    {sendingOtp ? 'Sending…' : 'Send OTP'}
                  </button>
                </div>
              </div>

              {otpSent && !otpVerified && (
                <div className="mt-3 flex items-center gap-2">
                  <input
                    type="text"
                    placeholder="Enter OTP"
                    value={emailOtp}
                    onChange={e => setEmailOtp(e.target.value)}
                    className="bg-gray-200 rounded-full px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-300 !border-none"
                    required
                  />
                  <button
                    type="button"
                    onClick={handleVerifyOtp}
                    disabled={verifyingOtp || !emailOtp}
                    className="px-3 py-2 bg-green-600 text-white rounded-full hover:opacity-90 disabled:opacity-50"
                  >
                    {verifyingOtp ? 'Verifying…' : 'Verify'}
                  </button>
                </div>
              )}

              {otpVerified && (
                <div className="mt-2 text-sm text-green-700">Email verified ✓</div>
              )}
            </div>

            <div className="flex flex-col">
              <label
                htmlFor="phoneNumber"
                className="text-gray-700 font-medium mb-1"
              >
                Phone Number <span className="text-red-500">*</span>
              </label>
              <div className="flex">
                <div className="flex items-center bg-gray-200 rounded-l-full px-4 py-2">
                  +91
                </div>
                <input
                  id="phoneNumber"
                  className="flex-1 bg-gray-200 rounded-r-full px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-300 !border-none"
                  value={formData.phoneNumber}
                  onChange={handleChange('phoneNumber')}
                  required
                />
              </div>
            </div>
          </div>
        );

      case 1:
        return (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="flex flex-col">
              <label htmlFor="gender" className="text-gray-700 font-medium mb-1">
                Gender <span className="text-red-500">*</span>
              </label>
              <select
                id="gender"
                className="w-full bg-gray-200 rounded-full px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-300 !border-none"
                value={formData.gender}
                onChange={handleChange('gender')}
                required
              >
                <option value="">Select Gender</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div className="flex flex-col">
              <label htmlFor="dateOfBirth" className="text-gray-700 font-medium mb-1">
                Date of Birth <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                id="dateOfBirth"
                className="w-full bg-gray-200 rounded-full px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-300 !border-none"
                value={formData.dateOfBirth}
                onChange={handleChange('dateOfBirth')}
                required
              />
            </div>

            <div className="flex flex-col">
              <label htmlFor="weight" className="text-gray-700 font-medium mb-1">
                Weight (kg)
              </label>
              <input
                id="weight"
                type="number"
                placeholder="Enter weight in kg"
                className="w-full bg-gray-200 placeholder-gray-500 rounded-full px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-300 !border-none"
                value={formData.weight}
                onChange={handleChange('weight')}
              />
            </div>

            <div className="flex flex-col">
              <label htmlFor="maritalStatus" className="text-gray-700 font-medium mb-1">
                Marital Status
              </label>
              <select
                id="maritalStatus"
                className="w-full bg-gray-200 rounded-full px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-300 !border-none"
                value={formData.maritalStatus}
                onChange={handleChange('maritalStatus')}
              >
                <option value="">Select Status</option>
                <option value="Unmarried">Unmarried</option>
                <option value="Divorced">Divorced</option>
                <option value="Widowed">Widowed</option>
                <option value="Separated">Separated</option>
              </select>
            </div>

            <div className="flex flex-col">
              <label htmlFor="religion" className="text-gray-700 font-medium mb-1">
                Religion <span className="text-red-500">*</span>
              </label>
              <select
                id="religion"
                className="w-full bg-gray-200 rounded-full px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-300 !border-none"
                value={formData.religion}
                onChange={handleChange('religion')}
                required
              >
                <option value="">Select Religion</option>
                {religions.map(religion => (
                  <option key={religion._id} value={religion.name}>
                    {religion.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex flex-col">
              <label htmlFor="caste" className="text-gray-700 font-medium mb-1">
                Caste
              </label>
              <select
                id="caste"
                className="w-full bg-gray-200 rounded-full px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-300 !border-none"
                value={formData.caste}
                onChange={handleChange('caste')}
              >
                <option value="">Choose Your Caste</option>
                {castes.map(caste => (
                  <option key={caste._id} value={caste.name}>
                    {caste.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex flex-col">
              <label htmlFor="motherTongue" className="text-gray-700 font-medium mb-1">
                Mother Tongue
              </label>
              <select
                id="motherTongue"
                className="w-full bg-gray-200 rounded-full px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-300 !border-none"
                value={formData.motherTongue}
                onChange={handleChange('motherTongue')}
              >
                <option value="">Select Mother Tongue</option>
                {motherTongues.map(tongue => (
                  <option key={tongue._id} value={tongue.name}>
                    {tongue.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {!formData.founderId && (
              <div className="flex flex-col">
                <label htmlFor="occupation" className="text-gray-700 font-medium mb-1">
                  Occupation <span className="text-red-500">*</span>
                </label>
                <select
                  id="occupation"
                  className="w-full bg-gray-200 rounded-full px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-300 !border-none"
                  value={formData.occupation}
                  onChange={handleChange('occupation')}
                  required
                >
                  <option value="">Select Occupation</option>
                  {occupations.map(o => <option key={o._id} value={o.title}>{o.title}</option>)}
                </select>
              </div>
            )}

            {isEntrepreneurOccupation(formData.occupation) && (
              <>
                <div className="flex flex-col">
                  <label htmlFor="companyName" className="text-gray-700 font-medium mb-1">
                    Startup / Company Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="companyName"
                    placeholder="Company or Startup name"
                    className="w-full bg-gray-200 placeholder-gray-500 rounded-full px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-300 !border-none"
                    value={formData.companyName}
                    onChange={handleChange('companyName')}
                    required={isEntrepreneurOccupation(formData.occupation)}
                  />
                </div>

                <div className="flex flex-col">
                  <label htmlFor="expectedRevenue" className="text-gray-700 font-medium mb-1">
                    Expected / Current Revenue
                  </label>
                  <input
                    id="expectedRevenue"
                    placeholder="e.g. Below 5 Lakh, 5-10 Lakh, or numeric"
                    className="w-full bg-gray-200 placeholder-gray-500 rounded-full px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-300 !border-none"
                    value={formData.expectedRevenue}
                    onChange={handleChange('expectedRevenue')}
                  />
                </div>

                <div className="flex flex-col col-span-2">
                  <label htmlFor="founderVision" className="text-gray-700 font-medium mb-1">
                    Founder Vision / Short Pitch <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="founderVision"
                    placeholder="Briefly describe your vision / product / goals"
                    className="w-full bg-gray-200 placeholder-gray-500 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-300 !border-none min-h-[80px]"
                    value={formData.founderVision}
                    onChange={handleChange('founderVision')}
                    required={isEntrepreneurOccupation(formData.occupation)}
                  />
                </div>
              </>
            )}

            <div className="flex flex-col">
              <label className="text-gray-700 font-medium mb-1">Country</label>
              <select
                value={formData.country}
                onChange={handleChange('country')}
                className="w-full bg-gray-200 rounded-full px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-300 !border-none"
              >
                <option value="">Select Country</option>
                {countries.map(c => <option key={c._id} value={c.name}>{c.name}</option>)}
              </select>
            </div>

            <div className="flex flex-col">
              <label className="text-gray-700 font-medium mb-1">State</label>
              <select
                value={formData.state}
                onChange={handleChange('state')}
                className="w-full bg-gray-200 rounded-full px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-300 !border-none"
              >
                <option value="">Select State</option>
                {states.map(s => <option key={s._id} value={s.name}>{s.name}</option>)}
              </select>
            </div>

            <div className="flex flex-col">
              <label className="text-gray-700 font-medium mb-1">City</label>
              <select
                value={formData.city}
                onChange={handleChange('city')}
                className="w-full bg-gray-200 rounded-full px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-300 !border-none"
              >
                <option value="">Select City</option>
                {cities.map(c => <option key={c._id} value={c.name}>{c.name}</option>)}
              </select>
            </div>

            <div className="flex flex-col">
              <label htmlFor="education" className="text-gray-700 font-medium mb-1">
                Education
              </label>
              <select
                id="education"
                className="w-full bg-gray-200 rounded-full px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-300 !border-none"
                value={formData.education}
                onChange={handleChange('education')}
              >
                <option value="">Select Education</option>
                {educations.map(education => (
                  <option key={education._id} value={education.name}>
                    {education.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex flex-col">
              <label htmlFor="annualIncome" className="text-gray-700 font-medium mb-1">
                Annual Income (INR)
              </label>
              <select
                id="annualIncome"
                className="w-full bg-gray-200 rounded-full px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-300 !border-none"
                value={formData.annualIncome}
                onChange={handleChange('annualIncome')}
              >
                <option value="">Select Income Range</option>
                <option value="Below 5 Lakh">Below 5 Lakh</option>
                <option value="5-10 Lakh">5-10 Lakh</option>
                <option value="10-20 Lakh">10-20 Lakh</option>
                <option value="20-50 Lakh">20-50 Lakh</option>
                <option value="Above 50 Lakh">Above 50 Lakh</option>
              </select>
            </div>

            <div className="flex flex-col">
              <label htmlFor="fatherName" className="text-gray-700 font-medium mb-1">
                Father's Name
              </label>
              <input
                id="fatherName"
                placeholder="Enter Father's Name"
                className="w-full bg-gray-200 placeholder-gray-500 rounded-full px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-300 !border-none"
                value={formData.fatherName}
                onChange={handleChange('fatherName')}
              />
            </div>

            <div className="flex flex-col">
              <label htmlFor="motherName" className="text-gray-700 font-medium mb-1">
                Mother's Name
              </label>
              <input
                id="motherName"
                placeholder="Enter Mother's Name"
                className="w-full bg-gray-200 placeholder-gray-500 rounded-full px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-300 !border-none"
                value={formData.motherName}
                onChange={handleChange('motherName')}
              />
            </div>

            <div className="flex flex-col">
              <label htmlFor="familyStatus" className="text-gray-700 font-medium mb-1">
                Family Status
              </label>
              <select
                id="familyStatus"
                className="w-full bg-gray-200 rounded-full px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-300 !border-none"
                value={formData.familyStatus}
                onChange={handleChange('familyStatus')}
              >
                <option value="">Select Family Status</option>
                <option value="Middle Class">Middle Class</option>
                <option value="Upper Middle Class">Upper Middle Class</option>
                <option value="Rich">Rich</option>
                <option value="Affluent">Affluent</option>
              </select>
            </div>

            <div className="flex flex-col">
              <label htmlFor="familyType" className="text-gray-700 font-medium mb-1">
                Family Type
              </label>
              <select
                id="familyType"
                className="w-full bg-gray-200 rounded-full px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-300 !border-none"
                value={formData.familyType}
                onChange={handleChange('familyType')}
              >
                <option value="">Select Family Type</option>
                <option value="Joint">Joint</option>
                <option value="Nuclear">Nuclear</option>
              </select>
            </div>

            <div className="flex flex-col">
              <label htmlFor="familyValues" className="text-gray-700 font-medium mb-1">
                Family Values
              </label>
              <select
                id="familyValues"
                className="w-full bg-gray-200 rounded-full px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-300 !border-none"
                value={formData.familyValues}
                onChange={handleChange('familyValues')}
              >
                <option value="">Select Family Values</option>
                <option value="Traditional">Traditional</option>
                <option value="Moderate">Moderate</option>
                <option value="Liberal">Liberal</option>
              </select>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-5">
            <div className="flex flex-col">
              <label htmlFor="aboutMe" className="text-gray-700 font-medium mb-1">
                About Me
              </label>
              <textarea
                id="aboutMe"
                placeholder="Tell us about yourself..."
                className="w-full bg-gray-200 placeholder-gray-500 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-300 !border-none min-h-[100px]"
                value={formData.aboutMe}
                onChange={handleChange('aboutMe')}
              />
            </div>

            <div className="flex flex-col">
              <label htmlFor="hobbies" className="text-gray-700 font-medium mb-1">
                Hobbies & Interests
              </label>
              <input
                id="hobbies"
                placeholder="e.g. Reading, Traveling, Sports"
                className="w-full bg-gray-200 placeholder-gray-500 rounded-full px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-300 !border-none"
                value={formData.hobbies}
                onChange={handleChange('hobbies')}
              />
            </div>

            <div className="flex flex-col">
              <label htmlFor="password" className="text-gray-700 font-medium mb-1">
                Password <span className="text-red-500">*</span>
              </label>
              <input
                type="password"
                id="password"
                placeholder="Enter Password"
                className="w-full bg-gray-200 placeholder-gray-500 rounded-full px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-300 !border-none"
                value={formData.password}
                onChange={handleChange('password')}
                required
              />
            </div>

            <div className="flex flex-col">
              <label htmlFor="confirmPassword" className="text-gray-700 font-medium mb-1">
                Confirm Password <span className="text-red-500">*</span>
              </label>
              <input
                type="password"
                id="confirmPassword"
                placeholder="Confirm Password"
                className="w-full bg-gray-200 placeholder-gray-500 rounded-full px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-300 !border-none"
                value={formData.confirmPassword}
                onChange={handleChange('confirmPassword')}
                required
              />
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="w-full min-h-screen bg-gradient-to-br from-red-500 via-red-300 to-yellow-100 flex items-center justify-center p-4 overflow-auto">
      <div className="w-full bg-white rounded-2xl shadow-2xl flex flex-col lg:flex-row min-h-[90vh]">
        <div className="lg:w-1/2 w-full bg-white px-4 py-6 overflow-y-auto">
          <Link to="/" className="flex items-center mb-4">
            <img src={Logo} alt="Logo" className="w-28" />
          </Link>
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-2">Matrimony Registration</h2>
          <p className="text-center text-gray-600 mb-6 h-6">{displayText}</p>

          <form onSubmit={handleSubmit} className="space-y-5 text-sm w-full mx-auto px-2 sm:px-4 md:px-6 lg:px-8 xl:px-10">
            {error && !verifyLoading && (
              <motion.div
                initial={{ y: -10, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.2 }}
                className="bg-red-100 text-red-700 p-3 rounded text-sm"
              >
                {error}
              </motion.div>
            )}
            {renderStepProgress()}

            <div className="max-h-[50vh] md:max-h-[60vh] overflow-y-auto pr-2">
              {renderCurrentStep()}
            </div>

            <div className="flex justify-between mt-8">
              {currentStep > 0 && (
                <button
                  type="button"
                  onClick={prevStep}
                  className="px-4 py-2 sm:px-6 sm:py-2 bg-gray-200 rounded-full flex items-center gap-2 hover:bg-gray-300"
                >
                  <ChevronLeft size={18} />
                  <span className="hidden sm:inline">Back</span>
                </button>
              )}
              {currentStep < steps.length - 1 ? (
                <button
                  type="button"
                  onClick={nextStep}
                  className="px-4 py-2 sm:px-6 sm:py-2 bg-[#4b154c] text-white rounded-full flex items-center gap-2 hover:opacity-90 ml-auto"
                  disabled={currentStep === 0 && !otpVerified}
                >
                  <span className="hidden sm:inline">Next</span>
                  <ChevronRight size={18} />
                </button>
              ) : (
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2 sm:px-6 sm:py-2 bg-[#4b154c] text-white rounded-full hover:opacity-90 disabled:opacity-50 ml-auto"
                >
                  {loading ? 'Registering…' : 'Complete Registration'}
                </button>
              )}
            </div>
          </form>

          <div className="text-center text-sm text-gray-600 mt-6">
            <p>By clicking Register Free, you agree to our <Link to="/terms" className="text-indigo-600 hover:underline">Terms & Conditions</Link></p>
            <p className="mt-2">Already a member? <Link to="/matrimony-login" className="text-indigo-600 hover:underline">Login Now</Link></p>
          </div>
        </div>

        <div className="lg:w-1/2 w-full relative hidden lg:block overflow-hidden">
          <img
            src="/matri_reg.jpg"
            alt="Matrimony Illustration"
            className="w-full h-full object-cover"
          />
          <Link to="/" className="absolute top-4 right-4 flex items-center gap-2 text-black font-bold">
            <FaBackward />
            <span className="hidden sm:inline">Back to Home</span>
          </Link>
        </div>

        <div className="lg:hidden fixed bottom-4 right-4">
          <Link to="/" className="bg-white p-3 rounded-full shadow-lg flex items-center justify-center">
            <FaBackward className="text-black" />
          </Link>
        </div>
      </div>
    </div>
  );
}